import { ARCHITECTURE_DIAGRAM, DIAGRAM_NODES, type DiagramNode } from './stackContent';
import { STACK_ICONS } from './stackIcons';

/** One 16-unit brand mark, drawn from Simple Icons' 24-unit path data. */
function Mark({ node, titleY }: { node: DiagramNode; titleY: number }) {
  if (node.mark === 'dot') {
    return <circle className="gp-fd-d-dot" cx={node.x + 20} cy={titleY - 5} r={6} />;
  }
  return (
    <g className="gp-fd-d-ico" transform={`translate(${node.x + 12} ${titleY - 13}) scale(${16 / 24})`}>
      <path d={STACK_ICONS[node.mark]} />
    </g>
  );
}

function Node({ node }: { node: DiagramNode }) {
  // Short boxes (the data column) sit their text a little higher than tall ones.
  const compact = node.h <= 50;
  const titleY = node.y + (compact ? 21 : 24);
  const subY = node.y + (compact ? 40 : 46);
  return (
    <g>
      <rect
        className={node.highlight ? 'gp-fd-d-box gp-fd-d-box-ai' : 'gp-fd-d-box'}
        x={node.x}
        y={node.y}
        width={node.w}
        height={node.h}
        rx={2}
      />
      <Mark node={node} titleY={titleY} />
      <text className="gp-fd-d-title" x={node.x + 34} y={titleY}>
        {node.title}
      </text>
      {node.subs.map((sub, i) => (
        <text key={sub} className="gp-fd-d-sub" x={node.x + 14} y={subY + i * 15}>
          {sub}
        </text>
      ))}
    </g>
  );
}

/**
 * The architecture diagram on /about/stack: people → edge and delivery → compute →
 * data and services. Ported from docs/tech-stack-overview.html. All colors come from
 * front-door tokens via stack.css, so it follows Cloudscape's light/dark mode. It has a
 * fixed minimum width; the page wraps it in a horizontally scrolling region.
 */
export function StackDiagram() {
  const { ariaLabel, lanes, edgeLabels: l } = ARCHITECTURE_DIAGRAM;
  const arrow = 'url(#gp-fd-d-arrow)';
  return (
    <svg className="gp-fd-diagram-svg" viewBox="0 0 1000 420" role="img" aria-label={ariaLabel}>
      <defs>
        <marker
          id="gp-fd-d-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path className="gp-fd-d-head" d="M0 0 L10 5 L0 10 z" />
        </marker>
      </defs>

      {lanes.map((lane) => (
        <text key={lane.label} className="gp-fd-d-lane" x={lane.x} y={412}>
          {lane.label}
        </text>
      ))}

      {/* Edges first, so the boxes sit on top. */}
      <path className="gp-fd-d-edge" d="M100 40 V18 H580 V38" markerEnd={arrow} />
      <text className="gp-fd-d-label" x={300} y={13}>
        {l.apiCalls}
      </text>
      <path className="gp-fd-d-edge" d="M180 70 H218" markerEnd={arrow} />
      <path className="gp-fd-d-edge" d="M100 110 V148" markerEnd={arrow} />
      <text className="gp-fd-d-label" x={108} y={134}>
        {l.signIn}
      </text>
      <path className="gp-fd-d-edge" d="M320 100 V128" markerEnd={arrow} />
      <path className="gp-fd-d-edge" d="M180 355 H218" markerEnd={arrow} />
      <path className="gp-fd-d-edge" d="M320 320 V192" markerEnd={arrow} />
      <text className="gp-fd-d-label" x={328} y={260}>
        {l.writesOnly}
      </text>
      <text className="gp-fd-d-label" x={328} y={274}>
        {l.appPrefix}
      </text>
      <path className="gp-fd-d-edge gp-fd-d-edge-dash" d="M420 85 H445 V270 H468" markerEnd={arrow} />
      <text className="gp-fd-d-label" x={438} y={222} textAnchor="end">
        {l.aiStreams}
      </text>
      <path className="gp-fd-d-edge" d="M580 100 V128" markerEnd={arrow} />
      {/* The bus from compute to the data column. */}
      <path className="gp-fd-d-edge" d="M690 170 H725" />
      <path className="gp-fd-d-edge" d="M690 270 H725" />
      <path className="gp-fd-d-edge" d="M725 65 V375" />
      {[65, 127, 189, 251, 313, 375].map((y) => (
        <path key={y} className="gp-fd-d-edge" d={`M725 ${y} H758`} markerEnd={arrow} />
      ))}

      {DIAGRAM_NODES.map((node) => (
        <Node key={node.id} node={node} />
      ))}
    </svg>
  );
}
